// High Frequency Word (sight word) assessment lists + helpers.
//
// HFW_GROUPS lives here (moved from HFWAssessment.js) so Assessment, Practice,
// Results, and Assessment Settings all share one source of truth — mirroring
// how cvcWords.js backs the CVC assessment + settings.
//
// Words themselves are common English (not copyrightable individually). The
// specific groupings (which word lands in which level) were curated for this
// app and are not derived from any proprietary curriculum or stimulus set.

export const HFW_GROUPS = {
  1: ['I', 'am', 'the', 'like', 'to', 'a', 'have', 'is', 'he', 'my', 'we', 'make', 'for', 'me', 'with', 'she', 'see', 'look'],
  2: ['are', 'that', 'of', 'they', 'you', 'do', 'one', 'two', 'three', 'four', 'five', 'here', 'go', 'from', 'yellow', 'blue', 'green', 'what'],
  3: ['said', 'was', 'where', 'come', 'play', 'any', 'down', 'her', 'how', 'away', 'give', 'little', 'funny', 'were', 'some', 'going', 'know', 'live'],
  4: ['find', 'again', 'over', 'pretty', 'all', 'now', 'black', 'brown', 'white', 'good', 'could', 'open', 'please', 'want', 'every', 'this', 'round', 'may'],
  5: ['be', 'saw', 'our', 'eat', 'soon', 'walk', 'who', 'there', 'into', 'out', 'so', 'then', 'new', 'too', 'when', 'say', 'under', 'no'],
};

export const HFW_GROUP_LEVELS = [1, 2, 3, 4, 5];

// What teachers see (v3.89.1): the five built-in lists are "Units". The
// stored fields and the code keep their names (`groupLevel`, `justThisGroup`,
// HFW_GROUPS) — result docs written before the rename read the same — and
// "Group" on a High Frequency Words page now only ever means a small group
// of students.
// A result with no usable unit on record reads "Unit", never "Unit undefined".
const unitNumber = (level) => (level === null || level === '' ? NaN : Number(level));
export const hfwUnitLabel = (level) => (Number.isFinite(unitNumber(level)) ? `Unit ${unitNumber(level)}` : 'Unit');
/** "Unit 1" for the first unit, "Units 1–3" for a cumulative test up to unit 3. */
export const hfwUnitRangeLabel = (level) => (unitNumber(level) > 1 ? `Units 1–${unitNumber(level)}` : 'Unit 1');
export const HFW_WORD_ORDERS = ['random', 'fixed'];
export const HFW_LETTER_CASES = ['asIs', 'lowercase', 'uppercase'];

// A custom sight word: letters only, 1–15 chars. Sight words are short
// real words ("the", "and", "I") — no spaces, digits, or punctuation.
export function isValidHfwWord(word) {
  if (typeof word !== 'string') return false;
  return /^[a-zA-Z]{1,15}$/.test(word.trim());
}

// Apply the configured letter case for display. 'asIs' preserves the curated
// casing (e.g. capital "I") exactly as authored.
export function applyHfwCase(word, letterCase) {
  if (letterCase === 'uppercase') return String(word).toUpperCase();
  if (letterCase === 'lowercase') return String(word).toLowerCase();
  return word;
}

// What the test page calls each case (v3.89.0). Anything applyHfwCase does
// not re-case is shown as written, so it is named that way too.
export const HFW_CASE_LABELS = Object.freeze({
  asIs: 'As written',
  lowercase: 'Lowercase letters',
  uppercase: 'Capital letters',
});

export function describeHfwCase(letterCase) {
  return letterCase === 'lowercase' || letterCase === 'uppercase' ? HFW_CASE_LABELS[letterCase] : HFW_CASE_LABELS.asIs;
}

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// Unordered base word list for the given HFW settings + chosen group level.
// `groupLevel` / `justThisGroup` (the unit, and "just this unit") come from the in-assessment selector (which
// the settings tab seeds the default for); order/case/custom come from
// hfwSettings. When a custom list is configured it overrides the units.
export function getHfwBaseWords({
  groupLevel = 1,
  justThisGroup = false,
  useCustomList = false,
  customWords = [],
} = {}) {
  if (useCustomList && Array.isArray(customWords) && customWords.length > 0) {
    return customWords.filter(isValidHfwWord);
  }
  const lvl = HFW_GROUP_LEVELS.includes(Number(groupLevel)) ? Number(groupLevel) : 1;
  if (justThisGroup) return [...(HFW_GROUPS[lvl] || [])];
  let words = [];
  for (let g = 1; g <= lvl; g++) words = words.concat(HFW_GROUPS[g] || []);
  return words;
}

// Presentation-ready word list: base list with the configured order applied.
// A custom list uses its own `customWordOrder` (teachers usually enter words
// in a deliberate teaching sequence); the built-in units use `wordOrder`.
// Letter case is applied at display time (so stored response keys stay
// canonical), mirroring how CvcAssessment handles letterCase.
export function getHfwWords(settings = {}) {
  const base = getHfwBaseWords(settings);
  const order = settings.useCustomList
    ? (settings.customWordOrder || 'fixed')
    : settings.wordOrder;
  return order === 'fixed' ? [...base] : shuffle(base);
}

// Default hfwSettings — matches today's behavior (Unit 1 cumulative, random
// order, curated casing, no custom list) so existing teachers see no change
// until they opt into different options. customWordOrder defaults to 'fixed'
// because a hand-entered list is usually meant to be presented in order.
export const DEFAULT_HFW_SETTINGS = {
  groupLevel: 1,
  justThisGroup: false,
  wordOrder: 'random',
  letterCase: 'asIs',
  useCustomList: false,
  customWords: [],
  customWordOrder: 'fixed',
};
